import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import riversideCabanasPicnicImage from "@/assets/riverside-cabanas-picnic.webp";
import floatTheRiverImage from "@/assets/float-the-river-family.webp";
import glampingSunsetImage from "@/assets/gallery-glamping-sunset.webp";

const ExperienceOptionsSection = () => {
  const experiences = [
    {
      image: riversideCabanasPicnicImage,
      title: "Day Trip",
      subtitle: "Cabanas & Picnic Tables",
      price: "Starting at $49",
      timeInfo: "10:30am - 6:30pm",
      description: "Rent a shaded riverside spot with BBQ pit, hammock, and seating for up to 15. Just show up, grill, float, and relax.",
      cta: "See Day Trip Options",
      link: "/book-now?plan=day",
    },
    {
      image: floatTheRiverImage,
      title: "Float the River",
      subtitle: "Tubing & Kayaking",
      price: "$29.99 unlimited all-day",
      timeInfo: "10:30am - 6:30pm",
      description: "1.5 miles of uncrowded spring-fed river. We handle the shuttle — you just float. No reservations needed.",
      cta: "Explore Tubing & Kayaking",
      link: "/book-now?plan=tubing",
    },
    {
      image: glampingSunsetImage,
      title: "Stay Overnight",
      subtitle: "Glamping & Log Cabins",
      price: "Starting at $49/night (weekdays)",
      timeInfo: "Check-In: 3:30pm Out: 11am",
      description: "Air-conditioned cabins with real beds, kitchenettes, fire pits, hammocks & BBQ grills. Fall asleep to the sound of the river.",
      cta: "See All Overnight Options",
      link: "/book-now?plan=overnight",
    },
  ];

  return (
    <section className="py-16 bg-background">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-brown mb-4">
            Plan your perfect <span className="text-river">Daycation</span> and/or <span className="text-river">Overnight Getaway</span>
          </h2>
          <p className="text-lg text-muted-foreground">
            Whether you want to hang out in a riverside Cabana for the day, float for an afternoon or stay for a few nights — we've got you covered.
          </p>
        </div>

        {/* Experience Cards */}
        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {experiences.map((experience, index) => (
            <div
              key={experience.title}
              className="group bg-card rounded-2xl overflow-hidden shadow-soft hover:shadow-elevated transition-all duration-500 border border-border"
            >
              {/* Image */}
              <div className="relative h-72 overflow-hidden">
                <img
                  src={experience.image}
                  alt={experience.subtitle}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  loading="lazy"
                  decoding="async"
                  width={400}
                  height={288}
                />
                
                <div className="absolute top-4 left-4">
                  <span className="px-3 py-1 rounded-full bg-golden text-brown text-sm font-cta font-semibold">
                    {experience.title}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 space-y-4">
                <div className="text-center">
                  <h3 className="font-heading text-xl lg:text-2xl font-semibold text-foreground mb-1">
                    {experience.subtitle}
                  </h3>
                  <p className="text-river font-cta font-semibold">
                    {experience.price}
                  </p>
                  <p className="text-copper text-sm font-medium">
                    {experience.timeInfo}
                  </p>
                </div>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {experience.description}
                </p>
                <Button variant="outline" className="w-full group/btn" asChild>
                  <Link 
                    to={experience.link} 
                    className="flex items-center justify-center gap-2"
                    aria-label={`${experience.cta} - ${experience.subtitle}`}
                  >
                    {experience.cta}
                    <ArrowRight className="h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
                  </Link>
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ExperienceOptionsSection;
